import secureApiService from './secureApiService'
import { Faculty, FacultyJSON } from '@/models/faculty.model'
import { PaginatedResponse, fetchAllPages } from '@/models/templates/pagination.model'

export interface FacultyMutationPayload {
  name: string
  description?: string | null
  is_active?: boolean
}

interface FacultiesResponseJSON {
  success: boolean
  message?: string
  data?: FacultyJSON[] | FacultyJSON | null
  meta?: {
    total?: number
    page?: number
    perPage?: number
  }
}

interface FacultySingleResponseJSON {
  success: boolean
  message?: string
  data?: FacultyJSON
}

class FacultiesService {
  private basePath = '/faculties'

  async getFacultiesPage(params: { page?: number; perPage?: number } = {}): Promise<PaginatedResponse<Faculty>> {
    const page = params.page ?? 1
    const perPage = params.perPage ?? 25
    const query = new URLSearchParams({ page: String(page), perPage: String(perPage) })

    const response = await secureApiService.get<FacultiesResponseJSON>(`${this.basePath}?${query.toString()}`, {
      requireAuth: true,
    })

    const payload = response.data
    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to fetch faculties')
    }

    const items = Array.isArray(payload.data) ? payload.data : payload.data ? [payload.data] : []
    const data = items.map((faculty) => Faculty.fromJSON(faculty))
    const meta = {
      total: payload.meta?.total ?? items.length,
      page: payload.meta?.page ?? page,
      perPage: payload.meta?.perPage ?? perPage,
    }

    return { data, meta }
  }

  async getAllFaculties(perPage: number = 50): Promise<PaginatedResponse<Faculty>> {
    return fetchAllPages((page) => this.getFacultiesPage({ page, perPage }), perPage)
  }

  async getFaculties(): Promise<Faculty[]> {
    const response = await this.getAllFaculties()
    return response.data
  }

  async createFaculty(payload: FacultyMutationPayload): Promise<Faculty> {
    const response = await secureApiService.post<FacultySingleResponseJSON>(this.basePath, {
      name: payload.name,
      description: payload.description,
      is_active: payload.is_active,
    })

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to create faculty')
    }

    return Faculty.fromJSON(body.data)
  }

  async updateFaculty(id: string, payload: FacultyMutationPayload): Promise<Faculty> {
    const response = await secureApiService.put<FacultySingleResponseJSON>(`${this.basePath}/${id}`, {
      name: payload.name,
      description: payload.description,
      is_active: payload.is_active,
    })

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to update faculty')
    }

    return Faculty.fromJSON(body.data)
  }

  async deleteFaculty(id: string): Promise<void> {
    const response = await secureApiService.delete<FacultySingleResponseJSON>(`${this.basePath}/${id}`)

    const body = response.data
    if (!body?.success) {
      throw new Error(body?.message || 'Unable to delete faculty')
    }
  }
}

export const facultiesService = new FacultiesService()
